// Tutoriales in-app (#1013) — estado del motor.
import { create } from "zustand";
import { isAbortError } from "../utils/api";
import { canSee } from "./eligibility";
import { fetchMyTutorials, postProgress } from "./service";
import type { ProgressAction, Tutorial, TutorialProgress, TutorialRules } from "./types";

const EMPTY_PROGRESS: TutorialProgress = { dontShowAny: false, dismissed: {}, completed: {} };

// Las escrituras de progreso van en fila: cada POST sale cuando terminó el
// anterior, y la respuesta del servidor sólo se adopta cuando ya no queda
// ninguna pendiente. Así "terminar" + "no volver a mostrar" (dos acciones
// seguidas) no se pisan: una respuesta vieja no borra la otra acción.
let progressQueue: Promise<void> = Promise.resolve();
let pendingWrites = 0;
let loadSeq = 0;

interface Active {
  tutorial: Tutorial;
  step: number;
  /** Abierto a mano (desde Ayuda o el botón "?"): no cuenta como ofrecido. */
  manual: boolean;
}

interface TutorialsState {
  /** Usuario para el que se cargaron las reglas (al cambiar de sesión se recarga). */
  loadedFor: string | null;
  rules: TutorialRules | null;
  progress: TutorialProgress | null;
  active: Active | null;
  /** Catálogo (se carga bajo demanda: arrastra la wiki completa). */
  catalog: Tutorial[] | null;
  /** Si el catálogo cargado incluye Sistemas Internos (admin de plataforma). */
  catalogWithInternal: boolean;
  ensureCatalog: () => Promise<Tutorial[]>;
  /** Falló /tutorials/me: la pantalla de Tutoriales ofrece reintentar. */
  loadError: boolean;
  /**
   * Tutoriales que el usuario cerró en ESTA sesión sin marcar "no volver a
   * mostrar": no se le vuelven a ofrecer hasta su próxima sesión.
   */
  skippedThisSession: string[];

  load: (userId: string, signal?: AbortSignal) => Promise<void>;
  reset: () => void;
  start: (id: string, opts?: { manual?: boolean }) => boolean;
  next: () => void;
  prev: () => void;
  /** Cierra el tutorial. `dontShowAgain` lo guarda como descartado. */
  close: (opts?: { dontShowAgain?: boolean; completed?: boolean }) => void;
  record: (action: ProgressAction, tutorialId?: string) => Promise<void>;
}

/** Progreso local tras una acción (mismo cálculo que el backend). */
function applyLocal(progress: TutorialProgress, action: ProgressAction, id?: string): TutorialProgress {
  const now = new Date().toISOString();
  const next: TutorialProgress = {
    dontShowAny: progress.dontShowAny,
    dismissed: { ...progress.dismissed },
    completed: { ...progress.completed },
  };
  if (action === "dismiss" && id) next.dismissed[id] = now;
  if (action === "complete" && id) next.completed[id] = now;
  if (action === "reset" && id) {
    delete next.dismissed[id];
    delete next.completed[id];
  }
  if (action === "dont-show-any") next.dontShowAny = true;
  if (action === "show-again") {
    next.dontShowAny = false;
    next.dismissed = {};
  }
  return next;
}

export const useTutorialsStore = create<TutorialsState>((set, get) => ({
  loadedFor: null,
  rules: null,
  progress: null,
  active: null,
  catalog: null,
  catalogWithInternal: false,
  loadError: false,
  skippedThisSession: [],

  ensureCatalog: async () => {
    // Sistemas Internos sólo entra al catálogo de un admin de plataforma, y
    // sólo entonces se descarga su chunk (import() dentro de loadTutorials).
    const withInternal = get().rules?.platformAdmin === true;
    const current = get().catalog;
    if (current && get().catalogWithInternal === withInternal) return current;
    const { loadTutorials } = await import("./catalog");
    const catalog = await loadTutorials(withInternal);
    // Si mientras tanto cambiaron las reglas (otra sesión), no se pisa nada.
    if ((get().rules?.platformAdmin === true) !== withInternal) return get().catalog || catalog;
    set({ catalog, catalogWithInternal: withInternal });
    return catalog;
  },

  load: async (userId, signal) => {
    if (get().loadedFor === userId && get().rules) return;
    const seq = ++loadSeq;
    set({ loadedFor: userId, loadError: false });
    try {
      const { progress, ...rules } = await fetchMyTutorials(signal);
      if (seq !== loadSeq || get().loadedFor !== userId) return; // cambió la sesión mientras tanto
      set({ rules, progress: progress || EMPTY_PROGRESS });
    } catch (err) {
      if (isAbortError(err)) {
        if (seq === loadSeq && get().loadedFor === userId) set({ loadedFor: null });
        return;
      }
      if (seq === loadSeq && get().loadedFor === userId) set({ loadedFor: null, loadError: true });
      // Sin reglas no se ofrece nada solo (fail-closed); la pantalla de
      // Tutoriales muestra el error y permite reintentar.
      console.warn("[tutorials] no se pudieron cargar las reglas", err);
    }
  },

  // Al cambiar de sesión se olvida también el catálogo: el de un admin trae
  // Sistemas Internos y no debe quedar para el siguiente usuario.
  reset: () =>
    set({
      loadedFor: null,
      rules: null,
      progress: null,
      active: null,
      catalog: null,
      catalogWithInternal: false,
      loadError: false,
      skippedThisSession: [],
    }),

  start: (id, { manual = false } = {}) => {
    const tutorial = get().catalog?.find((t) => t.id === id);
    if (!tutorial || !canSee(tutorial, get().rules)) return false;
    set({ active: { tutorial, step: 0, manual } });
    return true;
  },

  next: () => {
    const active = get().active;
    if (!active) return;
    if (active.step + 1 >= active.tutorial.steps.length) {
      get().close({ completed: true });
      return;
    }
    set({ active: { ...active, step: active.step + 1 } });
  },

  prev: () => {
    const active = get().active;
    if (!active || active.step === 0) return;
    set({ active: { ...active, step: active.step - 1 } });
  },

  close: ({ dontShowAgain = false, completed = false } = {}) => {
    const active = get().active;
    if (!active) return;
    const id = active.tutorial.id;
    set({ active: null, skippedThisSession: [...new Set([...get().skippedThisSession, id])] });
    // `record` encola: las dos acciones se guardan una tras otra, en orden.
    if (completed) void get().record("complete", id);
    if (dontShowAgain) void get().record("dismiss", id);
  },

  record: (action, tutorialId) => {
    // Optimista: la pantalla responde ya; si la API falla, al menos en esta
    // sesión se respeta lo que el usuario pidió.
    set({ progress: applyLocal(get().progress || EMPTY_PROGRESS, action, tutorialId) });
    pendingWrites += 1;
    const run = async () => {
      try {
        const progress = await postProgress(action, tutorialId);
        // Con otra escritura en fila, esta respuesta ya es vieja: la
        // siguiente trae el estado completo.
        if (pendingWrites === 1) set({ progress });
      } catch (err) {
        console.warn("[tutorials] no se pudo guardar el progreso", err);
      } finally {
        pendingWrites -= 1;
      }
    };
    const done = progressQueue.then(run);
    progressQueue = done;
    return done;
  },
}));

